<template>
  <v-card outlined class="server-clock">
    <v-card-text class="py-3">
      <v-row align="center" no-gutters>
        <!-- Título com ícone -->
        <v-col cols="12" sm="auto" class="d-flex align-center mb-2 mb-sm-0">
          <v-icon class="mr-2" :color="iconColor">mdi-clock-outline</v-icon>
          <span class="text-subtitle-1 font-weight-medium">Relógio</span>
        </v-col>

        <v-spacer class="d-none d-sm-flex" />

        <!-- Informações do relógio -->
        <v-col cols="12" sm="auto">
          <v-row no-gutters align="center" :class="{ 'justify-space-between': $vuetify.breakpoint.xsOnly }">
            <!-- Data -->
            <v-col cols="auto" class="mr-4 mr-sm-6">
              <div class="text-caption grey--text text--darken-1">Data:</div>
              <div class="text-body-2 font-weight-medium">{{ clockDate }}</div>
            </v-col>

            <!-- Hora -->
            <v-col cols="auto" class="mr-4 mr-sm-6">
              <div class="text-caption grey--text text--darken-1">Hora:</div>
              <div class="text-body-2 font-weight-medium clock-time">{{ clockTime }}</div>
            </v-col>

            <!-- Fuso -->
            <v-col cols="auto" class="mr-4 mr-sm-6">
              <div class="text-caption grey--text text--darken-1">Fuso:</div>
              <div class="text-body-2 font-weight-medium">{{ clockTimezone }}</div>
            </v-col>

            <!-- Status -->
            <v-col cols="auto">
              <div class="text-caption grey--text text--darken-1">Status:</div>
              <v-chip
                :color="statusColor"
                :text-color="statusTextColor"
                x-small
                label
              >
                {{ clockStatusLabel }}
              </v-chip>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { useServerClock } from '@/composables/useServerClock'

export default {
  name: 'EmcServerClock',

  props: {
    /**
     * Inicia o relógio automaticamente ao montar
     */
    autoStart: {
      type: Boolean,
      default: true
    },
    /**
     * Timeout em ms para sincronização com servidor
     */
    timeout: {
      type: Number,
      default: 5000
    }
  },

  data() {
    return {
      clock: null,
      // Estado local para reatividade
      clockDate: '--/--/----',
      clockTime: '--:--:--',
      clockTimezone: '---',
      clockStatus: 'loading',
      clockStatusLabel: 'Carregando...',
      updateInterval: null
    }
  },

  computed: {
    statusColor() {
      switch (this.clockStatus) {
        case 'synced':
          return 'success'
        case 'local':
          return 'warning'
        case 'error':
          return 'error'
        default:
          return 'grey lighten-2'
      }
    },
    statusTextColor() {
      return this.clockStatus === 'loading' ? 'grey darken-1' : 'white'
    },
    iconColor() {
      switch (this.clockStatus) {
        case 'synced':
          return 'success'
        case 'local':
          return 'warning'
        case 'error':
          return 'error'
        default:
          return 'grey'
      }
    }
  },

  mounted() {
    this.clock = useServerClock({
      timeout: this.timeout
    })

    if (this.autoStart) {
      this.start()
    }
  },

  beforeDestroy() {
    this.stop()
  },

  methods: {
    /**
     * Atualiza estado local a partir do composable
     */
    syncState() {
      if (this.clock) {
        this.clockDate = this.clock.state.date || '--/--/----'
        this.clockTime = this.clock.state.time || '--:--:--'
        this.clockTimezone = this.clock.state.timezone || '---'
        this.clockStatus = this.clock.state.status
        this.clockStatusLabel = this.clock.getStatusLabel()
      }
    },

    /**
     * Inicia o relógio
     */
    async start() {
      if (this.clock) {
        await this.clock.start()
        
        // Atualiza estado local periodicamente
        this.syncState()
        this.updateInterval = setInterval(() => {
          this.syncState()
        }, 100) // Atualiza a cada 100ms para UI fluída
        
        this.$emit('started')
        
        if (this.clock.state.status === 'synced') {
          this.$emit('synced')
        } else if (this.clock.state.status === 'local') {
          this.$emit('fallback')
        }
      }
    },

    /**
     * Para o relógio
     */
    stop() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval)
        this.updateInterval = null
      }
      if (this.clock) {
        this.clock.stop()
        this.$emit('stopped')
      }
    },

    /**
     * Reinicia sincronização
     */
    async refresh() {
      if (this.clock) {
        await this.clock.refresh()
        this.syncState()
      }
    }
  }
}
</script>

<style scoped>
.server-clock {
  background-color: #fafafa;
}

.clock-time {
  font-variant-numeric: tabular-nums;
  font-family: 'Roboto Mono', monospace;
}
</style>
